import React, {useState, useEffect} from 'react';
import Head from '../../../features/Components/Profile/Head';
import Social from '../../../features/Components/Profile/Social';
import ButtonMUI from '../../../shared/ButtonMUI';
import {Controller, useForm} from 'react-hook-form';
import InputMUI from '../../../shared/InputMUI';
import {useDispatch, useSelector} from 'react-redux';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import './UserDetails.scss';
import DropzoneMultiple from '../../Components/DropzoneMultiple';
import {
    getProfileMe,
    postProfileDetails,
    postProfileMeAvatar,
    deleteProfileMeAvatar
} from '../../../redux/actions/profile/profileActions';
import {notifySuccess} from '../../../helpers/notifySnack';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {useTranslation} from 'react-i18next';

const UserDetails = () => {
    const {t} = useTranslation();

    const dispatch = useDispatch();
    const {buttonLoading} = useSelector(({app}) => app);
    const [buttonLoad, setButtonLoad] = useState(false);
    const {profileMe} = useSelector((state) => state.profile);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [avatarType, setAvatarType] = useState('');

    const schema = yup.object({
        name: yup.string().required(t('validations.required')),
        phone: yup.string().required(t('validations.required')),
        email: yup.string().email(t('validations.email')).required(t('validations.required'))
    });

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: {errors, isValid}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            avatar: {},
            image: [],
            name: '',
            role: '',
            phone: '',
            email: ''
        }
    });

    useEffect(() => {
        if (profileMe && Object.keys(profileMe).length) {
            reset({
                name: profileMe.firstName ?? '',
                phone: profileMe.phone ?? '',
                email: profileMe.email ?? '',
                role: profileMe.roleName ?? '',
                avatar: {},
                image: []
            });

            if (profileMe.avatar && profileMe.avatar.main) {
                setAvatarUrl(profileMe.avatar.main);
                setAvatarType(profileMe.avatar.type);
            }
        }
    }, [profileMe]);

    const onFilesAdded = (files) => {
        setValue('image', files);
    };
    const onAvatarAdded = (avatar) => {
        setValue('avatar', avatar);
    };

    const onSubmit = async (data) => {
        try {
            setButtonLoad(true);

            const formData = new FormData();
            formData.append('name', data.name);
            formData.append('phone', data.phone);
            formData.append('email', data.email);
            const res = await dispatch(postProfileDetails(formData));

            if (res.payload && res.payload.status === 200) {
                if (data.image && data.image.length > 0) {
                    const imageFormData = new FormData();
                    imageFormData.append('avatar', data.image[0]);
                    await dispatch(postProfileMeAvatar(imageFormData));
                }

                if (data.avatar && Object.keys(data.avatar).length > 0) {
                    const avatarFormData = new FormData();
                    if (!data.avatar.id) {
                        return notifySuccess(t('all.successfully'));
                    }
                    avatarFormData.append('avatarId', data.avatar.id);
                    await dispatch(postProfileMeAvatar(avatarFormData));
                    setValue('avatar', {});
                } else if (data.image.length === 0) {
                    await dispatch(deleteProfileMeAvatar('avatar'));
                    return notifySuccess(t('all.successfully'));
                } else if (data.avatar && Object.keys(data.avatar).length <= 0 && data.image && data.image.length <= 0) {
                    await dispatch(deleteProfileMeAvatar('avatar'));
                    return notifySuccess(t('all.successfully'));
                }
                dispatch(getProfileMe());
                notifySuccess(t('all.successfully'));
            }
        } finally {
            setButtonLoad(false);
        }
    };

    const disabled = profileMe?.role === 'demo';


    const role = profileMe?.role
    return (
        <div className='user-details'>
            <Head title={t('profileDetails.title')}/>

            <div className='user-details__content'>
                <form className='user-details__form' onSubmit={handleSubmit(onSubmit)}>
                    <DropzoneMultiple
                        maxFiles={1}
                        maxSizeMb={50}
                        onFilesAdded={onFilesAdded}
                        onAvatarAdded={onAvatarAdded}
                        path={POPUPS.AVATAR_USER_POPUP}
                        initialImageUrl={avatarType === 'custom' ? avatarUrl : ''}
                        initialAvatarUrl={avatarType === 'default' ? avatarUrl : ''}
                        disabled={disabled}
                    />

                    <div className='form__input'>
                        <Controller
                            name='name'
                            control={control}
                            render={({field}) => (
                                <InputMUI
                                    type='text'
                                    fullWidth
                                    className='no-icons'
                                    disabled={disabled}
                                    error={errors.name?.message}
                                    inputProps={field}
                                    onChange={field.onChange}
                                    value={field.value}
                                    label={t('profileDetails.lastNameFirstName')}
                                />
                            )}
                        />
                    </div>

                    <div className='form__input'>
                        <InputMUI
                            type='text'
                            fullWidth
                            className='no-icons'
                            value={role ? t(`roles.${role}`) : ''}
                            label={t('profileDetails.role')}
                            disabled
                        />
                    </div>

                    <div className='form__input'>
                        <Controller
                            name='phone'
                            control={control}
                            render={({field, fieldState: {error}}) => (
                                <InputMUI
                                    className='no-icons'
                                    type='tel'
                                    fullWidth
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        let cleanValue = value.replace(/[^0-9+]/g, ''); // Remove anything that's not a digit or '+'

                                        if (cleanValue.charAt(0) !== '+') {
                                            cleanValue = cleanValue.replace(/\+/g, ''); // Remove any '+' that is not at the start
                                        } else {
                                            cleanValue = '+' + cleanValue.replace(/\+/g, ''); // Keep only the first '+' and remove others
                                        }

                                        if (/^\+?\d*$/.test(cleanValue)) {
                                            field.onChange(cleanValue);
                                        }
                                    }}
                                    value={field.value}
                                    error={error?.message}
                                    label={t('profileDetails.phone')}
                                    inputProps={field}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>

                    <div className='form__input'>
                        <Controller
                            name='email'
                            control={control}
                            render={({field}) => (
                                <InputMUI
                                    type='email'
                                    fullWidth
                                    onChange={field.onChange}
                                    value={field.value}
                                    className='no-icons'
                                    error={errors.email?.message}
                                    inputProps={field}
                                    label={t('profileDetails.email')}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>

                    <ButtonMUI disabled={!isValid || buttonLoading || disabled || buttonLoad} loading={buttonLoad}
                               formAction>
                        {t('profileDetails.save')}
                    </ButtonMUI>
                </form>
                <Social/>
            </div>
        </div>
    );
};

export default UserDetails;
